import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import useTable from 'hooks/useTable';
import MuiDataGrid from 'components/MuiDataGrid';
import { FormProps, IMedication } from '../PatientList/api/type';
import ListActionButtons from './ListActionButtons';
import MedicationDialog from './MedicationDialog';
import { formatStringToDate } from 'utils/formatting';

interface Props extends FormProps {
  defaultList?: IMedication[];
}

const MedicationList = (props: Props) => {
  const { setValue, defaultList } = props;
  const { palette } = useTheme();

  const { list, setList } = useTable<IMedication>();
  const [defaultValue, setDefaultValue] = useState<Partial<IMedication> | null>(
    null
  );

  useEffect(() => setList(defaultList ?? []), [defaultList, setList]);
  useEffect(() => {
    setValue(
      'medication',
      // Store object in API update format
      list.map(item => ({
        ...item,
        prescriptionTime: formatStringToDate(item.prescriptionTime),
        medicationTime: formatStringToDate(item.medicationTime),
      }))
    );
  }, [list, setValue]);

  const onCreateStart = () =>
    setDefaultValue({
      prescriptionTime: new Date().toString(),
      medicationTime: new Date().toString(),
    });
  const onCreateList = (data: IMedication) => setList(list.concat(data));
  const onUpdateList = (data: IMedication) =>
    setList(
      list.map(item =>
        item.ptMedicationNo === data.ptMedicationNo ? data : item
      )
    );

  const ActionButtons = ({ row }: { row: IMedication }) => {
    const onUpdate = () => setDefaultValue(row);
    const onDelete = () =>
      setList(list.filter(item => item.ptMedicationNo !== row.ptMedicationNo));

    return <ListActionButtons onUpdate={onUpdate} onDelete={onDelete} />;
  };

  const columns: GridColDef[] = [
    { field: 'ptMedicationNo', headerName: '用藥序號' },
    {
      field: 'prescriptionTime',
      headerName: '處方時間',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    {
      field: 'medicationTime',
      headerName: '給藥時間',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    { field: 'medicationId', headerName: '成分碼' },
    { field: 'medicationName', headerName: '通用名' },
    {
      align: 'right',
      field: 'actions',
      headerName: '',
      renderCell: ({ row }) => <ActionButtons row={row} />,
    },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" sx={{ color: `${palette.primary.main}` }}>
            用藥紀錄（可編輯）
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#555555a4',
              marginLeft: '30px',
            }}
          >
            （如需新增，請聯繫 SmartNurse ENR。）
          </Typography>
          {/* <Button
            size="small"
            variant="outlined"
            onClick={onCreateStart}
            sx={{ ml: 3 }}
          >
            Add
          </Button> */}
        </Box>

        <MuiDataGrid
          columns={columns}
          rows={list.map(item => ({ ...item, id: item.ptMedicationNo }))}
        />
      </Stack>

      {defaultValue && (
        <MedicationDialog
          isUpdate={!!defaultValue.ptMedicationNo}
          defaultValue={defaultValue}
          onClose={() => setDefaultValue(null)}
          onSearchNo={(no: number) =>
            list.find(item => item.ptMedicationNo === no) ? true : false
          }
          onSave={defaultValue.ptMedicationNo ? onUpdateList : onCreateList}
        />
      )}
    </Container>
  );
};

export default MedicationList;
